import config from "config/config";
import i18next, { i18n, TFunction } from "i18next";
import { initReactI18next } from "react-i18next";
import { localizedDateParts } from "./timings";

export enum ContentNS {
    ui = "ui",
    checklist = "checklist",
    time = "time",
    consent = "consent",
    passport = "passport",
    docs = "docs",
    countryCodes = "country-codes",
    nationalities = "nationalities",
    errors = "errors",
    usaStates = "usa-states"
}

const namespaces = Object.values(ContentNS)

interface StringMap {[key: string]: string;}

// Whitelabel sub-brands encode a parent client with "_" (see config/AGENTS.md).
// Each require() below must use a literal `process.env.X || "DEFAULT"`
// expression inline -- not a variable or loop -- so webpack's DefinePlugin
// can fold it and narrow the bundled files to just that one client.
function requireClientContent(locale: string, ns: string) {
  try {
    return require(`./locales-${process.env.REACT_APP_CLIENT || "DEFAULT"}/${locale}/${ns}.json`)
  } catch (e) {
    // fall through
  }

  try {
    return require(`./locales-${process.env.REACT_APP_CLIENT_PARENT || "DEFAULT"}/${locale}/${ns}.json`)
  } catch (e) {
    // fall through
  }

  return require(`./locales-DEFAULT/${locale}/${ns}.json`)
}

const resources: {[key: string]: StringMap} = {}
config.supportedLocales.forEach((locale: string) => {
    const nsDict: StringMap = {}

    namespaces.forEach(ns=> {
        nsDict[ns] = requireClientContent(locale, ns)
    })

    resources[locale] = nsDict
  })

const languageOf = (locale: string) => locale.split("-")[0].toLowerCase();

// Where i18next looks when a key is missing from the active locale. This has to
// be derived from the build rather than hardcoded: clients bundle different
// locale sets, so naming a fixed "en-US" resolves to nothing on a pack that
// ships bare codes (JetBlue: "en"/"es") and the raw key renders in the UI.
// Order: another region of the same language, then any English pack in the
// build, then the build's default locale — all of them locales we bundled.
export function fallbackLocales(
  locale: string | undefined,
  supportedLocales: string[],
  defaultLocale: string
): string[] {
  const sameLanguage = locale
    ? supportedLocales.filter(
        (supported) => languageOf(supported) === languageOf(locale)
      )
    : [];
  const english = supportedLocales.filter(
    (supported) => languageOf(supported) === "en"
  );

  return Array.from(
    new Set([...sameLanguage, ...english, defaultLocale])
  ).filter((fallback) => fallback !== locale);
}

class Content {
  static shared: Content = new Content();
  ready: boolean = false;
  instance: i18n = i18next;
  t: TFunction = i18next.t;
  private htmlLangSyncRegistered = false;

  private constructor() {}

  async setup(locale: string, callback: () => void) {
    // Keep <html lang> in sync with the active UI language. The lang in
    // index.html only covers first paint, so after a language switch it would
    // otherwise stay stale. Syncing it lets CSS :lang() size elements per
    // language (e.g. the passport scan/upload buttons) and lets assistive tech
    // announce the language actually shown. Registered before init so the
    // initial language is captured too, and guarded so we register exactly one
    // listener per runtime: setup() can run again before content assets finish
    // loading (a locale change, React StrictMode's double-invoke, HMR), and
    // i18next never clears observers across init(), so an unguarded on() would
    // stack duplicate listeners and redundant DOM writes.
    if (!this.htmlLangSyncRegistered) {
      i18next.on("languageChanged", (lng: string) => {
        document.documentElement.lang = lng;
      });
      this.htmlLangSyncRegistered = true;
    }

    await i18next
    .use(initReactI18next)
    .init({
        lng: locale,
        resources,
        fallbackLng: (code?: string) =>
          fallbackLocales(code, config.supportedLocales, config.defaultLocale),
        ns: Object.values(ContentNS),
        defaultNS: namespaces[0],
        debug: false,
      });

    // Unlike the languageChanged listener above, the formatters are re-added on
    // every setup() call by design: init() rebuilds services.formatter each time
    // and drops custom formatters, so guarding these would lose them on re-init.
    // Register before the callback fires: it gates content rendering, so the
    // formatter must exist before any {{val, localizedDate}} interpolation runs.
    // It pulls the locale-correct date pieces from Intl, then renders the
    // template held in the active lang pack (the key named by the `formatKey`
    // option, defaulting to "date-format"). The arrangement and literal text
    // thus live in the locale files per client/language, not hardcoded here.
    i18next.services.formatter?.add("localizedDate", (value, lng, options) => {
      const formatKey = options?.formatKey ?? "date-format";
      return i18next.t(formatKey, {
        ns: ContentNS.ui,
        lng,
        defaultValue: "{{weekday}}, {{day}} {{month}} {{year}}",
        ...localizedDateParts(value as Date, lng ?? "en-US"),
      });
    });

    callback();
  }

  changeLocale(locale: string) {
    this.instance.changeLanguage(locale)
  }
}

export default Content;
